import {
  ClientEntity,
  OperationalEntity
} from "@common-core/coat-operational-hierarchy-appsync-model";

/**
 * Validates tenant data integrity across the entire client hierarchy and extracts the tenant ID
 * from the ultimate parent's mapped operational entities.
 *
 * Validation: Ensures all tenant IDs across the entire hierarchy (ultimate parent + all descendants) are the same.
 * Extraction: Returns the tenant ID from the ultimate parent only.
 *
 * Handles four distinct flows:
 * 1. "Known Tenant": The client entity has an ultimate parent with one or more operational entities within a single tenant → Returns that tenant ID.
 * 2. "New Tenant Creation": The client entity is the ultimate parent (no ancestors) with no mapped operational entities → Returns null (tenant needs creation).
 * 3. "No Tenancy Established" Error: The client has ancestors, but the ultimate parent either has no mapped OEs or all mapped OEs lack tenancy → Throws error.
 * 4. "Multiple Tenants" Error: Different tenant IDs exist across the hierarchy → Throws error.
 * 5. "Data Integrity" Error: The client is the ultimate parent with mapped OEs but none have tenancy → Throws error.
 *
 * @param source - The client entity with hierarchy
 * @returns The tenant ID if one exists, or null if a new tenant needs to be created
 * @throws Error if tenancy cannot be established or multiple tenants detected across the hierarchy
 */
export const validateAndExtractTenantId = (
  source: ClientEntity
): string | undefined => {
  // source.hierarchy refers to the ultimate parent, or we can default to the source itself if there is no hierarchy attribute.
  const ultimateParent = source.hierarchy || source;

  // Check if the source entity has ancestors by looking at custParentId or custUltimateParentId
  const hasAncestors = !!(source.custParentId || source.custUltimateParentId);

  /**
   * Extracts non-null, non-empty tenant IDs from an array of operational entities
   */
  const extractTenantIds = (entities?: OperationalEntity[] | null): string[] =>
    (entities || [])
      .map(oe => oe.tenantId)
      .filter((id): id is string => id != null && id !== "");

  /**
   * Recursively collects all tenant IDs from an entity and all its descendants
   */
  const collectAllTenantIds = (entity: ClientEntity): string[] => {
    const tenantIds: string[] = [];

    // Collect tenant IDs from this entity's mapped operational entities
    tenantIds.push(...extractTenantIds(entity.mappedOperationalEntities));

    // Recursively collect from children
    if (entity.children) {
      entity.children.forEach(child => {
        tenantIds.push(...collectAllTenantIds(child));
      });
    }

    return tenantIds;
  };

  // Collect all tenant IDs across the entire hierarchy
  const allTenantIds = collectAllTenantIds(ultimateParent);

  // Check for data integrity: ensure only one unique tenant ID exists across the hierarchy
  const uniqueTenantIds = new Set(allTenantIds);

  // Case 4: Multiple Tenants Error
  if (uniqueTenantIds.size > 1) {
    throw new Error(
      `This client hierarchy is already associated with multiple tenants: ${Array.from(uniqueTenantIds).join(", ")}. Data integrity error.`
    );
  }

  // Now extract tenant IDs from the ultimate parent only (for return value)
  const mappedEntities = ultimateParent.mappedOperationalEntities;
  const ultimateParentTenantIds = extractTenantIds(mappedEntities);

  if (ultimateParentTenantIds.length > 0) {
    // Case 1: "Known Tenant" - Ultimate parent has OE(s) within a single tenant
    return ultimateParentTenantIds[0];
  }

  // Case 2: "New Tenant Creation" - Entity is the ultimate parent with no mapped OEs
  if (!hasAncestors) {
    // If the ultimate parent has mapped OEs but none have tenancy, this is a data integrity error
    if (mappedEntities && mappedEntities.length > 0) {
      throw new Error(
        `Data integrity error for ${source.caId}. The ultimate parent has mapped operational entities, but none have tenancy established.`
      );
    }
    // No mapped OEs - downstream logic will create the tenant when this function returns undefined.
    return undefined;
  }

  // Case 3: "No Tenancy Established" Error - Entity has ancestors, but they have no tenancy.
  if (!mappedEntities || mappedEntities.length === 0) {
    throw new Error(
      `Cannot create or assign a tenant for ${source.caId}. The ultimate parent has no mapped operational entities. Tenancy must be established on the ultimate parent.`
    );
  } else {
    throw new Error(
      `Cannot create or assign a tenant for ${source.caId}. The ultimate parent has mapped operational entities, but none have tenancy established. Tenancy must be established on the ultimate parent.`
    );
  }
};
